import dateize from '@sdk/helpers/formatters/dateize';
import monetize from '@sdk/helpers/formatters/monetize';
import patternize from '@sdk/helpers/formatters/patternize';
import { empty } from '@sdk/helpers/misc/nullable';

export default {
  computed: {
    fields() {
      return this.schema.fields;
    },
    sections() {
      return this.schema.sections;
    },
    subsections() {
      return this.schema.subsections;
    },

    visible() {
      return Object
        .values(this.schema.sections || {})
        .filter(section => section.visible)
        .map(
          section => ({
            ...section,
            fields: Object
              .values(section.fields || {})
              .filter(field => field.visible && field.type !== 'hidden'),
          }),
        )
        .filter(section => section.fields.length);
    },

    invalid() {
      return Object
        .values(this.visible)
        .some(section => Object
          .values(section.fields)
          .some(field => field.errors.length));
    },
  },
  methods: {
    getJSON(payload) {
      const array = Array.isArray(payload);

      return Object.keys(payload).reduce((reduced, i) => {
        const {
          constant,
          field,
          getter,
        } = payload[i];

        if (constant) {
          reduced[i] = constant;
        } else if (field) {
          const {
            value,
          } = this.fields[field];

          const got = getter ? getter.call(this, value) : value;

          if (array) {
            if (!empty(got)) {
              reduced.push(got);
            }
          } else {
            reduced[i] = got;
          }
        } else {
          reduced[i] = this.getJSON(payload[i]);
        }

        return reduced;
      }, array ? [] : {});
    },

    setJSON(payload, json, blank = false) {
      /*
        recursive function to populate this.fields
        with a inputted json object's values

        GIVEN:
          payload = {
            children_1: { field: 'children_1_local_identifier' },
            parent_1: {
              children_2: { field: 'children_2_local_identifier' },
              children_3: { field: 'children_3_local_identifier' },
              parent_2: {
                children_4: { field: 'children_4_local_identifier' },
              },
            },
          };

          json = {
            children_1: 'chidren_1_value',
            parent_1: {
              children_2: 'chidren_2_value',
              children_3: 'chidren_3_value',
              parent_2: {
                children_4: 'chidren_4_value',
              },
            },
          };

        setJSON(payload, json);
          BEFORE:
            this.fields = {
              children_1_local_identifier: { value: null },
              children_2_local_identifier: { value: null },
              children_3_local_identifier: { value: null },
              children_4_local_identifier: { value: null },
            };

          AFTER:
            this.fields = {
              children_1_local_identifier: { value: 'chidren_1_value' },
              children_2_local_identifier: { value: 'chidren_2_value' },
              children_3_local_identifier: { value: 'chidren_3_value' },
              children_4_local_identifier: { value: 'chidren_4_value' },
            };
      */

      return Object.keys(json).forEach((i) => {
        let payloaded = payload[i];
        let value = json[i];

        if (/^\d{4}-\d{2}-\d{2}/.test(value)) {
          value = dateize(value);
        }

        if (!payloaded && payload[`${i}_id`] && value && value.id) {
          /*
            handling the case where some fields
            are sent in the request as:
              { field_id: 123 }
            but returned in the response as:
              { field: { id: 123 } }
          */

          value = value.id;
          payloaded = payload[`${i}_id`];
        }

        if (!payloaded || (empty(value) && !blank)) {
          /*
            blank=true
              empties local fields values in case its
              values are empty on the inputted json object

            blank=false
              preserves local fields values even if its
              values are empty on the inputted json object

            GIVEN:
              payload = { foo: { field: 'bar' } };
              json = { foo: NULL };

            setJSON(payload, json, TRUE);
              BEFORE:
                this.fields = { bar: { value: 'This field is filled from the start' } };
              AFTER:
                this.fields = { bar: { value: NULL } };

            setJSON(payload, json, FALSE);
              BEFORE:
                this.fields = { bar: { value: 'This field is filled from the start' } };
              AFTER:
                this.fields = { bar: { value: 'This field is filled from the start' } };
          */

          return;
        }

        const {
          field,
          reveal,
          setter,
          silent,
        } = payloaded;

        if (field) {
          /*
            field.setter is a helper function that may be used
            to manipulate values before setting it to the fields

            it's usefull to handle server-format and
            local-format localization in a automated way
            (like turning 11/01/2020 into 2020-11-01, etc)

            GIVEN:
              payload = { foo: { field: 'bar', setter: value => value - 10 } };
              json = { foo: 30 };

            setJSON(payload, json);
              BEFORE:
                this.fields = { bar: { value: null } };
              AFTER:
                this.fields = { bar: { value: 20 } };
          */

          this.updateValue(
            field,
            setter ? setter.call(this, value, json) : value,
            { silent },
          );

          if (reveal) {
            /*
              reveal=true
                makes the field visible in case it was
                hidden (as we are filling it)
              reveal=false
                keeps the field hidden (in case it already is)
            */

            this.updateVisible(field, true, { clear: false, silent });
          }
        } else if (!empty(json[i])) {
          /*
            recursive call in case the current
            iteration object is a parent
          */

          this.setJSON(payload[i], json[i], blank);
        }
      });
    },

    updateValue(id, value = null, options = { silent: false }) {
      const {
        mask,
        value: previous,
        callback,
      } = this.fields[id];

      let formatted = value;

      if (mask) {
        switch (mask.type) {
          case 'patternize':
            formatted = patternize(formatted, mask);
            break;
          case 'monetize':
            formatted = monetize(formatted, mask);
            break;
          default:
            break;
        }
      }

      if (formatted === previous) {
        return;
      }

      this.fields[id].value = formatted;
      this.updateErrors(id, []);

      if (callback) {
        if (options.silent) {
          return;
        }

        callback(formatted, previous);
      }
    },

    updateVisible(id, visible, options = { clear: true, silent: false }) {
      this.fields[id].visible = !!visible;

      if (options.clear) {
        this.updateValue(id, null, options);
        this.updateErrors(id, [], options);
      }
    },

    updateDisabled(id, disabled) {
      this.fields[id].disabled = !!disabled;
    },

    updateCallback(id, callback) {
      this.fields[id].callback = callback;
    },

    updateOptions(id, list) {
      this.fields[id].options = list;
    },

    updateLoading(id, loading) {
      this.fields[id].loading = !!loading;
    },

    updateValidations(id, validations) {
      this.fields[id].validations = validations;
    },

    updateLabel(id, label) {
      this.fields[id].label = label;
    },

    updatePlaceholder(id, placeholder) {
      this.fields[id].placeholder = placeholder;
    },

    updateSize(id, size) {
      this.fields[id].size = size;
    },

    updateErrors(id, errors) {
      this.fields[id].errors = Array.isArray(errors) ? errors : [errors];
    },
    updateErrorsRecursively(errors, schema) {
      if (!errors || !schema) {
        return;
      }

      Object.keys(errors).forEach((i) => {
        if (!schema[i]) {
          return;
        }

        if (schema[i].field) {
          const first = errors[i] && errors[i][0];

          if (first.message) {
            this.updateErrors(schema[i].field, first.message);
          } else if (first.error) {
            this.updateErrors(schema[i].field, first.error);
          } else if (typeof first === 'string') {
            this.updateErrors(schema[i].field, first);
          } else {
            try {
              // non-existing fields (temporary)
              const fallback = Object.values(first)[0][0];

              this.updateErrors(schema[i].field, fallback.message || fallback.error || fallback);
            } catch (err) { /* */ }
          }
        } else {
          this.updateErrorsRecursively(errors[i], schema[i]);
        }
      });
    },

    updateSectionToggled(id, toggled) {
      this.sections[id].toggled = toggled;
    },
  },
};
